export const frontmatter = {
  title: "The developer's way of life",
  description: "We talk about salaries and stacks. We don't talk about the mental load, the imposter syndrome, or the slow burn. Let's be honest about what this job actually costs.",
  date: "2023-07-11",
  tags: ["career", "development", "opinions"],
  readingTime: 6,
};

From the outside, we've got it good. The salaries are great, the demand is high, we get to build the future from a laptop. The job looks like a clean, logical career story.

That's the surface. The reality is messier and there's an invisible tax most of us pay quietly. The stuff we don't talk about at standup, the things that don't show up in a perf review but show up in every line of code we write.

I'm talking about the mental load that doesn't switch off. The little voice that says you're a fraud. The slow fade of burnout that pulls the joy out of the craft. None of it is a sign of weakness. It's an occupational hazard.

## The mental load: 100 browser tabs in your head

Try explaining your day to someone outside tech. Almost impossible. Because the hardest part of the job isn't the typing — it's the *holding*.

At any moment, a developer is balancing a stack of context that would crash a lesser machine. Picture 100 browser tabs, all open:

- The legacy codebase you're in, with all its quirks and undocumented "don't touch this."
- The new API you're integrating, with its slightly-wrong docs.
- The specific business logic for the ticket you're on.
- Why CI failed this morning.
- A vague memory of a Slack thread from three weeks ago that's suddenly relevant.
- Plus the other 95: edge cases, naming decisions, architectural patterns you have to keep in mind.

Now picture someone Slacks you mid-flow with "hey, can you look at this real quick?" That isn't a five-minute interruption. It's a full crash. You have to save the state of all 100 tabs, open 100 new ones for their problem, solve it, and then try to reload the original — knowing you've lost half of it.

<Callout emoji="🧠">
  This is why you'll catch us staring at a wall for 20 minutes. We're not slacking. We're garbage-collecting our own brain. The mental load doesn't clock out at 6 PM.
</Callout>

## Imposter syndrome: the "any day now" feeling

Here's the dirty secret of senior software work: the more you know, the more you feel like a fraud. You'd think experience brings confidence. It does, in some places. But experience also gives you a horrifyingly clear picture of the giant ocean of stuff you *don't* know.

Imposter syndrome is the steady, low hum that says you don't belong here, that your wins were luck, and that any day someone's going to find out.

It gets triggered by tiny things:

- A junior solves in 10 minutes the bug you've been stuck on for hours.
- A planning meeting where everyone throws around an acronym you've never heard, so you nod along while frantically Googling it.
- Reading the source of a popular open-source library and thinking "I could never write code this clean."
- A PR comment your brain reads as "you're a terrible programmer," even though it just said "this could be more efficient."

This isn't a junior thing. I know staff engineers and architects at major companies who feel this way. In a field that moves this fast, we're all professional beginners. The most powerful sentence a senior can learn isn't a complex algorithm — it's "I don't know, but I can find out."

## Burnout: the slow fade

People think burnout is 80-hour weeks. That's part of it. But the more common, more dangerous version isn't a flameout. It's a slow, quiet fade.

It's the gradual erosion of your energy and your interest. A thousand little cuts, not one big wound. It looks like:

- **The "everything is urgent" culture.** When every ticket is P0 and every project is a fire, your adrenaline gets stuck on. Eventually your brain just stops responding.
- **No agency.** You get handed fully-specced tickets with no say in the why or how. You're a pair of hands. Your problem-solving brain starts to atrophy.
- **The boulder.** Working on a legacy system where every fix introduces two new bugs. You're busy every day, but the rock keeps rolling back.

The real red flag isn't feeling stressed. It's feeling *nothing*. Opening your editor on a genuinely interesting problem and feeling… tired. Soul-tired. Passion gone. That's the warning.

## What actually helps

Telling a burned-out dev to "take a vacation" is a band-aid on a broken leg. You'll come back to the same broken environment. The actual fixes are less glamorous and harder.

1. **Protect your focus.** Block "no-meeting" time on your calendar. Turn off notifications. Document what's in your head so you can offload it. Learn to say "not right now."
2. **Talk about imposter syndrome.** Find one person you trust and ask them, "do you ever feel like you have no idea what you're doing?" I promise you the answer is yes. Also keep a private "brag doc" — a list of things you've actually shipped. Read it when the voice gets loud.
3. **Set hard boundaries.** Log off. Don't check Slack at 10 PM. Push back on unrealistic deadlines. And if the problem is the environment, the only real fix is to *change the environment*. Your health is worth more than any job.

These aren't weaknesses. They're the predictable cost of a profession that asks for a huge amount of invisible, high-stakes mental work. You're not broken for feeling this way. You're a software engineer.

We're all in this together. Now go close a few of those mental tabs. You've earned it.
